import { defineStore } from 'pinia'
import { ref } from 'vue'
import quotaApi from '@/io.ox/core/api/quota'
import folderApi from '@/io.ox/core/folder/api'
import filesApi from '@/io.ox/files/api'
import mailApi from '@/io.ox/mail/api'
import ox from '@/ox'
import _ from '@/underscore'
import keychain from '@/io.ox/keychain/api'

export const useQuotaStore = defineStore('quota', () => {
  const using = { mail: false, files: false, accounts: false }

  const mailQuota = ref({ quota: -1, use: 0, countquota: -1, countuse: 0 })
  const fileQuota = ref({ quota: -1, use: 0, countquota: -1, countuse: 0 })
  const accountQuota = {}

  function updateMailQuota (model) {
    setQuota({ module: 'mail', ...model.toJSON() })
  }

  function updateFileQuota (model) {
    setQuota({ module: 'files', ...model.toJSON() })
  }

  function updateAccountQuota (model) {
    setQuota(model.toJSON())
  }

  function sanitize ({ quota = -1, use = 0, countquota = -1, countuse = 0 } = {}) {
    return {
      quota: typeof quota === 'number' && quota > 0 ? quota : -1,
      use: typeof use === 'number' && use >= 0 ? use : 0,
      countquota: typeof countquota === 'number' && countquota > 0 ? countquota : -1,
      countuse: typeof countuse === 'number' && countuse >= 0 ? countuse : 0
    }
  }

  function reloadQuota () {
    quotaApi.reload()
  }

  // debounced because some accounts takes some seconds to update the quota on their side. (HiDrive)
  const reloadAccountQuota = _.debounce(() =>
    Object.keys(accountQuota).forEach(module =>
      Object.keys(module).forEach(accountId =>
        quotaApi.getAccountQuota(accountId, module, false)
      )
    )
  , 5000)

  // eslint-disable-next-line camelcase
  function setQuota ({ module = 'mail', use = 0, quota = -1, countuse = 0, countquota = -1, account_id = undefined } = {}) {
    let target
    switch (module) {
      case 'mail':
        target = mailQuota
        break
      case 'files':
        target = fileQuota
        break
      default:
        // eslint-disable-next-line camelcase
        target = accountQuota[module][account_id]
        break
    }

    Object.assign(target.value, sanitize({ use, quota, countuse, countquota }))
  }

  if (ox.debug) window.setQuota = setQuota

  return {

    getMailQuota () {
      if (!using.mail) {
        using.mail = true
        quotaApi.mailQuota.on('change', updateMailQuota)
        folderApi.on('cleared-trash', reloadQuota)
        mailApi.on('refresh.all deleted-mails-from-trash', reloadQuota)
        quotaApi.load().then(data => { mailQuota.value = data.mail })
      }
      return mailQuota
    },

    getFileQuota () {
      if (!using.files) {
        using.files = true
        quotaApi.fileQuota.on('change', updateFileQuota)
        quotaApi.requestFileQuotaUpdates()
        folderApi.on('clear', reloadQuota)
        filesApi.on('add:file remove:file add:version remove:version copy', reloadQuota)
        quotaApi.load().then(data => { fileQuota.value = data.file })
      }
      return fileQuota
    },

    getAccountQuota (module, accountId) {
      if (!accountQuota[module]) accountQuota[module] = {}
      if (!accountQuota[module][accountId]) {
        accountQuota[module][accountId] = ref({ quota: -1, use: 0, countquota: -1, countuse: 0 })
        quotaApi.getAccountModel(accountId, module).on('change', updateAccountQuota)
        quotaApi.getAccountQuota(accountId, module, false)

        if (!using.accounts) {
          using.accounts = true
          folderApi.on('clear', reloadAccountQuota)
          filesApi.on('add:file remove:file add:version remove:version copy', reloadAccountQuota)
          keychain.on('refresh.all refresh.list', reloadAccountQuota)
        }
      }
      return accountQuota[module][accountId]
    },

    // for testing purposes
    _setQuota: setQuota
  }
})
